import type { CSSProperties, ReactNode } from "react";
import { BilliardBallIcon } from "./billiard-ball-icon";

type Name =
  | "tools"
  | "layouts"
  | "cue-tip"
  | "drills"
  | "lessons"
  | "weaknesses"
  | "bowlard"
  | "count-nine"
  | "break-speed"
  | "set-match"
  | "events"
  | "people"
  | "players"
  | "stores"
  | "shop";

// One monochrome, rounded line style for both card artwork and small service icons.
const symbols: Record<Exclude<Name, "bowlard" | "count-nine">, ReactNode> = {
  tools: (
    <path d="M29.4 12.6a2 2 0 0 0 0 2.8l3.2 3.2a2 2 0 0 0 2.8 0l7.54-7.54a12 12 0 0 1-15.88 15.88L13.24 40.76a4.24 4.24 0 0 1-6-6l13.82-13.82a12 12 0 0 1 15.88-15.88l-7.54 7.54Z" />
  ),
  layouts: (
    <>
      <path d="M3 3h26a16 16 0 0 1 16 16v26M3 3v24.3M20.7 45H45" />
      <path d="M3 27.3h6.1c.5 0 .9-.2 1.3-.6l9-9.3a8 8 0 0 1 11.1 10.8l-9.3 9.3c-.4.4-.5.9-.5 1.5v6" />
      <path d="M14.2 22.9c.4 7.7 3.5 10.4 10.9 10.7M3 20.4h13.5M19.7 3v14.2M30.5 28.2H45M27.6 31.1V45" />
    </>
  ),
  "cue-tip": (
    <>
      <circle cx="24" cy="24" r="19" />
      <circle cx="24" cy="24" r={8 / 3} fill="currentColor" stroke="none" />
      <circle cx="29.66" cy="33.8" r="3.3" fill="currentColor" stroke="none" />
    </>
  ),
  drills: (
    <>
      <path d="m7 10-4 2v27c7-2 14-1 19 3h4c5-4 12-5 19-3V12l-4-2" />
      <path d="M24 11C19 6 12 5 7 7v27c6-2 12-1 17 4 5-5 11-6 17-4V7c-5-2-12-1-17 4Zm0 0v27" />
      <path d="M12 16c3 0 5 1 7 3M12 23c3 0 5 1 7 3M29 19c2-2 4-3 7-3M29 26c2-2 4-3 7-3" />
    </>
  ),
  lessons: (
    <>
      <rect x="5" y="5" width="38" height="28" rx="3" />
      <path d="M17 43 24 33l7 10M24 33v10M12 13h10M12 20h7" />
      <path d="m29 12 8 6-8 6Z" />
    </>
  ),
  weaknesses: (
    <>
      <path d="M24 3v4M8 10l3 3M37 13l3-3" />
      <path d="M17 31a12 12 0 1 1 14 0c-2 1.5-2 3-2 5H19c0-2 0-3.5-2-5Z" />
      <path d="M19 41h10M22 45h4M24 35v-9l-4-4m4 4 4-4" />
    </>
  ),
  "break-speed": (
    <>
      <path d="M11.28 36.72a18 18 0 1 1 25.44 0M16 40h16" />
      <circle cx="24" cy="26" r="3" />
      <path d="m26 23.8 7-8.8" />
    </>
  ),
  "set-match": (
    <>
      <rect x="4" y="9" width="40" height="30" rx="4" />
      <path d="M24 9v30M10 5v8M38 5v8" />
      <ellipse cx="14.5" cy="24" rx="3.5" ry="5" />
      <path d="M30 21.5c0-3.3 7-3.3 7 0 0 2-2 3-3.5 4.5L30 29h7" />
    </>
  ),
  events: (
    <>
      <rect x="6" y="9" width="36" height="33" rx="4" />
      <path d="M15 5v8M33 5v8M6 19h36m-26 9 6 6 11-11" />
    </>
  ),
  people: (
    <>
      <circle cx="18" cy="15" r="7" />
      <path d="M4 41v-5a14 14 0 0 1 28 0v5M32 8a7 7 0 0 1 0 14m6 5a12 12 0 0 1 6 10v4" />
    </>
  ),
  players: (
    <>
      <circle cx="24" cy="16" r="8" />
      <path d="M8 42v-2a16 16 0 0 1 32 0v2" />
    </>
  ),
  stores: (
    <>
      <path d="M6 20v22h36V20M4 14l5-8h30l5 8v3a5 5 0 0 1-10 0 5 5 0 0 1-10 0 5 5 0 0 1-10 0 5 5 0 0 1-10 0v-3Z" />
      <path d="M4 14h40M14 6v8M24 6v8M34 6v8M12 42V28h12v14M31 28h5v7h-5z" />
    </>
  ),
  shop: (
    <>
      <path d="M9 15h30l3 27H6l3-27Z" />
      <path d="M17 18V12a7 7 0 0 1 14 0v6M19 29l4 4 7-8" />
    </>
  ),
};

export function PracticeToolIcon({
  name,
  size = 48,
  style,
}: {
  name: Name;
  size?: number;
  style?: CSSProperties;
}) {
  if (name === "bowlard" || name === "count-nine") {
    return (
      <BilliardBallIcon
        number={name === "bowlard" ? 10 : 9}
        size={size}
        style={style}
        monochrome
      />
    );
  }
  return (
    <svg
      style={style}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {symbols[name]}
    </svg>
  );
}
